/**
 * A patient's clinical record (ADM-004). The first request goes without a reason, which a
 * clinician's own review queue allows. When the Worker answers `reason_required`, the screen asks
 * for one and nothing about the patient is shown until the record opens. The reason is
 * percent-encoded into its header, since a header value must be Latin-1.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminPatientIdentity, AdminPatientRecord } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import type { Result } from "./api.ts";
import { api } from "./api.ts";
import { Context, CopyValue, RecordLink, Truncate } from "./bits.tsx";
import type { Column } from "./layout.tsx";
import { Facts, PageHead, Panel, Section, Split, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { ReasonForm } from "./ReasonForm.tsx";
import { href } from "./router.ts";
import { BackToResults, ErrorText, Status } from "./ui.tsx";
import { day, refusal } from "./words.ts";
const VALUE = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 3
});
type Observation = AdminPatientRecord["observations"][number];
function flagOf(o: Observation) {
  switch (o.flag) {
    case "low":
      return <Status tone="warn">Low</Status>;
    case "high":
      return <Status tone="warn">High</Status>;
    case "abnormal":
      return <Status tone="danger">Abnormal</Status>;
    case "normal":
      return <span className="x1jty25b">In range</span>;
    case null:
      return null;
  }
}
function valueOf(o: Observation) {
  const value = o.valueNumeric === null ? o.valueText ?? "" : VALUE.format(o.valueNumeric);
  const outside = o.flag !== null && o.flag !== "normal";
  return <span {...{
    0: {},
    1: {
      className: "xqu410z"
    }
  }[!!outside << 0]}>{value}</span>;
}

/** The one collection date every result shares, or null when they differ. */
function sharedDate(record: AdminPatientRecord): string | null {
  const dates = new Set(record.observations.map(o => o.collectedOn));
  const [only] = dates;
  return dates.size === 1 && only !== undefined ? only : null;
}
const DOCUMENT = {
  stored: "Waiting to be read",
  extracting: "Being read",
  extracted: "Imported",
  failed: "Could not be read. Ask the customer for a PDF."
};
function load(id: string, reason: string | null): Promise<Result<AdminPatientRecord>> {
  return api<AdminPatientRecord>(ROUTES.adminPatient, reason === null ? {
    params: {
      patientId: id
    }
  } : {
    params: {
      patientId: id
    },
    headers: {
      "x-access-reason": encodeURIComponent(reason)
    }
  });
}
function fullName(record: AdminPatientRecord): string {
  const name = [record.patient.firstName, record.patient.lastName].filter(part => part !== null).join(" ");
  return name === "" ? "Name not given" : name;
}
const SEX = {
  female: "Female",
  male: "Male"
};
function Details({
  record
}: {
  readonly record: AdminPatientRecord;
}) {
  const {
    patient
  } = record;
  return <>
      <Section title="Details">
        <Facts items={[["Patient id", <CopyValue key="id" value={patient.id} label="patient id" />], ["Date of birth", day(patient.dateOfBirth)], ["Sex", patient.sex === null ? "Not recorded" : SEX[patient.sex]], ["Account", patient.userId === null ? "Not claimed" : <RecordLink to={href.user(patient.userId)} relation="Account" label={patient.email ?? "Open account"} />], ["Created", day(patient.createdAt)]]} />
      </Section>
    </>;
}
const collectedColumn: Column<Observation> = {
  label: "Collected",
  numeric: true,
  phoneLabel: true,
  cell: o => day(o.collectedOn)
};
function Record({
  record
}: {
  readonly record: AdminPatientRecord;
}) {
  const shared = sharedDate(record);
  return <>
      <PageHead back={<BackToResults />} title={fullName(record)} meta={record.openedBy === "queue" ? "Opened from your review queue. This opening is logged." : "Opened with your reason. This opening is logged."} />
      <Split side={<>
            <Context title="This record" who={fullName(record)} role={record.patient.email ?? "No account email"} can="You can read this record. Opening it is logged. You cannot edit the labs." links={[...(record.patient.userId === null ? [] : [{
        relation: "Account",
        label: record.patient.email ?? "Account",
        to: href.user(record.patient.userId)
      }]), ...record.links.map(link => ({
        relation: "Clinic",
        label: link.organizationName,
        to: href.organization(link.organizationId)
      }))]} />
            <Details record={record} />
          </>} main={<>
            <Section title="Lab results" count={record.observations.length}>
              {shared === null ? null : <p className="x1ghz6dp x1jty25b">All collected {day(shared)}</p>}
              <Table label="Lab results" rows={record.observations} rowKey={o => o.id} empty="No results read from this patient's labs yet." columns={[{
          label: "Marker",
          cell: o => o.name
        }, {
          label: "Value",
          numeric: true,
          cell: valueOf
        }, {
          label: "Unit",
          nowrap: true,
          cell: o => <span className="x1jty25b">{o.unit ?? ""}</span>
        }, {
          label: "Flag",
          nowrap: true,
          cell: flagOf
        }, {
          label: "Reference",
          phoneLabel: true,
          nowrap: true,
          cell: o => o.referenceText ?? <span className="x1jty25b">None printed</span>
        }, ...(shared === null ? [collectedColumn] : [])]} />
            </Section>
            <Section title="Lab files" count={record.documents.length}>
              <Table label="Lab files" rows={record.documents} rowKey={d => d.id} empty="No lab files uploaded." columns={[{
          label: "File",
          cell: d => <Truncate value={d.filename} />
        }, {
          label: "Status",
          cell: d => DOCUMENT[d.status]
        }, {
          label: "Collected",
          numeric: true,
          phoneLabel: true,
          cell: d => day(d.collectedOn)
        }, {
          label: "Uploaded",
          numeric: true,
          phoneLabel: true,
          cell: d => day(d.createdAt)
        }]} />
            </Section>
            <Section title="Clinics" count={record.links.length}>
              <Table label="Clinics" rows={record.links} rowKey={l => l.organizationId} empty="Not linked to any clinic." columns={[{
          label: "Clinic",
          cell: l => <RecordLink to={href.organization(l.organizationId)} relation="Clinic" label={l.organizationName} />
        }, {
          label: "Status",
          nowrap: true,
          cell: l => l.status === "active" ? <Status tone="ok">Active</Status> : l.status === "pending" ? <Status tone="warn">Pending</Status> : <Status tone="neutral">Removed</Status>
        }]} />
            </Section>
          </>} />
    </>;
}
function Gate({
  id,
  patient,
  onOpen
}: {
  readonly id: string;
  readonly patient: AdminPatientIdentity | null;
  readonly onOpen: (record: AdminPatientRecord) => void;
}) {
  const name = patient?.name ?? "this patient";
  async function open(reason: string): Promise<string | null> {
    const result = await load(id, reason);
    if (!result.ok) return refusal(result.error);
    onOpen(result.data);
    return null;
  }
  return <>
      <PageHead back={<BackToResults />} title={patient?.name ?? "Patient record"} meta={<>
            <span>{patient?.email ?? "No account email"}</span>
            <span>Patient {id}</span>
          </>} />
      <div className="x27kpxv">
        <Panel padded>
          <div className="x78zum5 xdt5ytf x1r0x45p">
            <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">Why are you opening {name}'s patient record?</h2>
            <p className="x1ghz6dp x1jty25b">
              This record is not in your review queue, so it opens only once you give a reason.
            </p>
            <ReasonForm id="access-reason" hint="Logged with your name. Include the ticket number." action="Open patient record" autoFocus onSubmit={open} />
          </div>
        </Panel>
      </div>
    </>;
}
export function PatientRecord({
  id
}: {
  readonly id: string;
}) {
  const [loaded, replace] = useLoad(`patient-${id}`, () => load(id, null));
  if (loaded.state === "loading") return <PageHead title="Opening record" />;
  if (loaded.state === "ready") return <Record record={loaded.data} />;
  if (loaded.error === "reason_required") return <Gate id={id} patient={loaded.patient} onOpen={replace} />;
  return <>
      <PageHead back={<BackToResults />} title="Patient record" />
      <ErrorText>{refusal(loaded.error)}</ErrorText>
    </>;
}